import React, { useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { Context } from '~/prix'
import { APIProvider, API } from '~/prix/react/hooks/api'
import tokenParsedToUser from '../utils/shared/tokenParsedToUser'
import { useKeycloak } from './keycloak'
import { identifyAnalyticsUser } from '~/utils/client/analytics'

const date = new Date()

export default function ApiWithContext({
  children,
  entities,
  sources,
}: Omit<API, 'isLoading' | 'context'> & { children: React.ReactNode }) {
  const { token, tokenParsed, isLoading } = useKeycloak()

  const context: Context = useMemo(
    () => ({
      ip: '127.0.0.1',
      userAgent: navigator.userAgent,
      timezoneOffset: date.getTimezoneOffset(),
      token: token ?? null,
      user: tokenParsed && tokenParsed.sub ? tokenParsedToUser(tokenParsed) : null,
      bypassAuthorization: false,
      requestId: null,
      userLogHelper: null,
    }),
    [isLoading, token, tokenParsed],
  )

  useEffect(() => {
    if (!context.user) {
      Sentry.setUser(null)
      return
    }

    Sentry.setUser({ id: String(context.user.id) })
    Sentry.setTag('user_role', context.user.roles[0] ?? 'unknown')
    Sentry.setTag('regional_unit', context.user.stateAbbreviation ?? 'national')

    identifyAnalyticsUser({
      email: context.user.email,
      id: String(context.user.id),
      roles: context.user.roles,
      regionalUnit: context.user.stateAbbreviation ? String(context.user.stateAbbreviation) : null,
    })
  }, [context.user])

  return (
    <APIProvider
      value={{
        entities,
        sources,
        context,
        isLoading,
      }}
    >
      {children}
    </APIProvider>
  )
}
